import { Navigate, useNavigate } from "react-router-dom";

import { homePathForUser, useAuth } from "../auth/AuthContext";
import {
  canChooseAccountMode,
  homePathForMode,
  readPreferredMode,
  staffModeLabel,
  writePreferredMode,
  type AccountMode,
} from "../auth/accountMode";

/**
 * «¿Cómo quieres entrar?» (Eduardo 2026-10-04): la cuenta que organiza y
 * también juega elige su lado al entrar. La última elección de este
 * navegador viene marcada (botón principal y con el foco) para entrar con
 * Enter. Las cuentas de un solo lado nunca la ven: van directo a su inicio.
 */
export default function ChooseAccountModePage() {
  const { user, isImpersonating } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;
  if (isImpersonating || !canChooseAccountMode(user)) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  const account = user;
  const staffLabel = staffModeLabel(account);
  const preferred: AccountMode = readPreferredMode() ?? "staff";

  function choose(mode: AccountMode) {
    writePreferredMode(mode);
    navigate(homePathForMode(account, mode), { replace: true });
  }

  const options: Array<{ mode: AccountMode; label: string; hint: string }> = [
    {
      mode: "staff",
      label: `Como ${staffLabel}`,
      hint: "Tus torneos, inscripciones, resultados y avisos.",
    },
    {
      mode: "player",
      label: "Como jugador",
      hint: "Tus partidos, posiciones y tu perfil de jugador.",
    },
  ];

  return (
    <section className="card account-mode-choice" aria-labelledby="account-mode-choice-title">
      <h1 id="account-mode-choice-title">¿Cómo quieres entrar?</h1>
      <p className="muted">
        Hola, {account.full_name}. Tu cuenta organiza y también juega. Después puedes cambiar desde el
        encabezado.
      </p>
      <div className="account-mode-choice__options">
        {options.map((option) => {
          const isPreferred = option.mode === preferred;
          return (
            <button
              key={option.mode}
              type="button"
              className={`btn account-mode-choice__option${isPreferred ? "" : " btn--ghost"}`}
              data-preferred={isPreferred ? "true" : undefined}
              autoFocus={isPreferred}
              onClick={() => choose(option.mode)}
            >
              <strong>{option.label}</strong>
              <span className="account-mode-choice__hint">{option.hint}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
